import React from "react";
import { Add as AddIcon } from "@mui/icons-material";
import { ParameterValue } from "../../types/TestTarget";

interface ParameterSectionProps {
  parameters: ParameterValue[] | undefined;
  usedParameters: Set<string>;
  mode: "view" | "edit" | "create";
  onAddParameter: () => void;
  onRemoveParameter: (index: number) => void;
  onUpdateParameter: (
    index: number,
    field: keyof ParameterValue,
    value: string
  ) => void;
  parameterErrors?: Map<number, string>;
}

export const ParameterSection: React.FC<ParameterSectionProps> = ({
  parameters = [],
  usedParameters,
  mode,
  onAddParameter,
  onRemoveParameter,
  onUpdateParameter,
  parameterErrors = new Map(),
}) => {
  return (
    <div style={{ width: "280px", flexShrink: 0 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <h4
          style={{
            margin: 0,
            fontSize: "0.85rem",
            fontWeight: "600",
            color: "#1e293b",
          }}
        >
          パラメータ ({parameters.length})
        </h4>
        {mode !== "view" && (
          <button
            onClick={onAddParameter}
            style={{
              padding: "2px 6px",
              fontSize: "0.7rem",
              background: "#667eea",
              color: "white",
              border: "none",
              borderRadius: "3px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "2px",
            }}
          >
            <AddIcon style={{ fontSize: "0.7rem" }} />
            追加
          </button>
        )}
      </div>

      <div style={{ height: "calc(100% - 32px)", overflowY: "auto" }}>
        {mode === "view" ? (
          <div
            style={{
              padding: "8px",
              background: "#f8fafc",
              borderRadius: "4px",
              border: "1px solid #e2e8f0",
            }}
          >
            {parameters.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                {parameters.map((param, index) => {
                  const isUsed = usedParameters.has(param.name);
                  return (
                    <div
                      key={index}
                      style={{
                        padding: "8px",
                        background: isUsed ? "#f0f4ff" : "#fff",
                        border: isUsed
                          ? "1px solid #667eea"
                          : "1px solid #d1d5db",
                        borderRadius: "4px",
                        fontSize: "0.7rem",
                        position: "relative",
                      }}
                    >
                      {isUsed && (
                        <div
                          style={{
                            position: "absolute",
                            top: "2px",
                            right: "2px",
                            fontSize: "0.55rem",
                            color: "#667eea",
                            fontWeight: "600",
                            background: "#fff",
                            padding: "1px 3px",
                            borderRadius: "2px",
                            border: "1px solid #667eea",
                          }}
                        >
                          使用中
                        </div>
                      )}
                      <div
                        style={{
                          fontWeight: "600",
                          color: "#374151",
                          marginBottom: "2px",
                        }}
                      >
                        {`{{${param.name}}}`}
                      </div>
                      {param.default_value && (
                        <div
                          style={{
                            color: "#6b7280",
                            fontSize: "0.65rem",
                            marginBottom: "2px",
                          }}
                        >
                          デフォルト値: {param.default_value}
                        </div>
                      )}
                      {param.description && (
                        <div
                          style={{
                            color: "#9ca3af",
                            fontSize: "0.6rem",
                            fontStyle: "italic",
                          }}
                        >
                          {param.description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  color: "#9ca3af",
                  fontStyle: "italic",
                  fontSize: "0.75rem",
                }}
              >
                パラメータが登録されていません
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            {parameters.length > 0 ? (
              parameters.map((param, index) => {
                const isUsed = usedParameters.has(param.name);
                const errorMessage = parameterErrors.get(index);
                const hasError = !!errorMessage;
                return (
                  <div
                    key={index}
                    style={{
                      padding: "10px",
                      paddingRight: "75px",
                      border: hasError
                        ? "1px solid #ef4444"
                        : isUsed
                        ? "1px solid #667eea"
                        : "1px solid #e2e8f0",
                      borderRadius: "6px",
                      background: hasError
                        ? "#fef2f2"
                        : isUsed
                        ? "#f0f4ff"
                        : "#f8fafc",
                      position: "relative",
                      fontSize: "0.75rem",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    {isUsed && (
                      <div
                        style={{
                          position: "absolute",
                          top: "2px",
                          right: "30px",
                          fontSize: "0.6rem",
                          color: "#667eea",
                          fontWeight: "600",
                          background: "#fff",
                          padding: "1px 3px",
                          borderRadius: "2px",
                          border: "1px solid #667eea",
                        }}
                      >
                        使用中
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      {/* パラメータ名フィールド */}
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.65rem",
                            fontWeight: "600",
                            color: "#374151",
                            marginBottom: "2px",
                          }}
                        >
                          パラメータ名{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          value={param.name}
                          onChange={(e) =>
                            onUpdateParameter(index, "name", e.target.value)
                          }
                          placeholder="例: 姓, メールアドレス"
                          style={{
                            border: hasError
                              ? "1px solid #ef4444"
                              : "1px solid #e5e7eb",
                            background: "#ffffff",
                            borderRadius: "3px",
                            padding: "4px 6px",
                            fontWeight: "600",
                            fontSize: "0.75rem",
                            width: "100%",
                            outline: "none",
                            transition:
                              "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                          }}
                          onFocus={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.style.borderColor = hasError
                              ? "#ef4444"
                              : "#667eea";
                            target.style.boxShadow = hasError
                              ? "0 0 0 2px rgba(239, 68, 68, 0.1)"
                              : "0 0 0 2px rgba(102, 126, 234, 0.1)";
                          }}
                          onBlur={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.style.borderColor = hasError
                              ? "#ef4444"
                              : "#e5e7eb";
                            target.style.boxShadow = "none";
                          }}
                        />
                        {errorMessage ? (
                          <div
                            style={{
                              fontSize: "0.6rem",
                              color: "#ef4444",
                              marginTop: "2px",
                              lineHeight: "1.2",
                              fontWeight: "500",
                            }}
                          >
                            {errorMessage}
                          </div>
                        ) : (
                          <div
                            style={{
                              fontSize: "0.6rem",
                              color: "#6b7280",
                              marginTop: "1px",
                              lineHeight: "1.2",
                            }}
                          >
                            ステップで {`{{${param.name || "パラメータ名"}}}`}{" "}
                            として使用
                          </div>
                        )}
                      </div>

                      {/* デフォルト値フィールド */}
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.65rem",
                            fontWeight: "500",
                            color: "#374151",
                            marginBottom: "2px",
                          }}
                        >
                          デフォルト値{" "}
                          <span
                            style={{
                              color: "#9ca3af",
                              fontWeight: "400",
                            }}
                          >
                            (任意)
                          </span>
                        </label>
                        <input
                          value={param.default_value}
                          onChange={(e) =>
                            onUpdateParameter(
                              index,
                              "default_value",
                              e.target.value
                            )
                          }
                          placeholder="例: 山田, test@example.com"
                          style={{
                            border: "1px solid #e5e7eb",
                            background: "#ffffff",
                            borderRadius: "3px",
                            padding: "4px 6px",
                            fontSize: "0.7rem",
                            color: "#6b7280",
                            width: "100%",
                            outline: "none",
                            transition:
                              "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                          }}
                          onFocus={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.style.borderColor = "#667eea";
                            target.style.boxShadow =
                              "0 0 0 2px rgba(102, 126, 234, 0.1)";
                          }}
                          onBlur={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.style.borderColor = "#e5e7eb";
                            target.style.boxShadow = "none";
                          }}
                        />
                        <div
                          style={{
                            fontSize: "0.6rem",
                            color: "#6b7280",
                            marginTop: "1px",
                            lineHeight: "1.2",
                          }}
                        >
                          テスト時の初期値として使用
                        </div>
                      </div>

                      {/* 説明フィールド */}
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.65rem",
                            fontWeight: "500",
                            color: "#374151",
                            marginBottom: "2px",
                          }}
                        >
                          説明{" "}
                          <span
                            style={{
                              color: "#9ca3af",
                              fontWeight: "400",
                            }}
                          >
                            (任意)
                          </span>
                        </label>
                        <input
                          value={param.description || ""}
                          onChange={(e) =>
                            onUpdateParameter(
                              index,
                              "description",
                              e.target.value
                            )
                          }
                          placeholder="例: ユーザーの姓を入力"
                          style={{
                            border: "1px solid #e5e7eb",
                            background: "#ffffff",
                            borderRadius: "3px",
                            padding: "4px 6px",
                            fontSize: "0.65rem",
                            color: "#9ca3af",
                            width: "100%",
                            outline: "none",
                            transition:
                              "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                          }}
                          onFocus={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.style.borderColor = "#667eea";
                            target.style.boxShadow =
                              "0 0 0 2px rgba(102, 126, 234, 0.1)";
                          }}
                          onBlur={(e) => {
                            const target = e.target as HTMLInputElement;
                            target.style.borderColor = "#e5e7eb";
                            target.style.boxShadow = "none";
                          }}
                        />
                        <div
                          style={{
                            fontSize: "0.6rem",
                            color: "#6b7280",
                            marginTop: "1px",
                            lineHeight: "1.2",
                          }}
                        >
                          パラメータの用途や注意事項
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => onRemoveParameter(index)}
                      style={{
                        position: "absolute",
                        top: "2px",
                        right: "2px",
                        background: "#ffffff",
                        border: "1px solid #ef4444",
                        borderRadius: "50%",
                        color: "#ef4444",
                        cursor: "pointer",
                        width: "16px",
                        height: "16px",
                        fontSize: "0.7rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: "1",
                        transition: "all 0.15s ease-in-out",
                      }}
                      onMouseEnter={(e) => {
                        const target = e.target as HTMLButtonElement;
                        target.style.background = "#ef4444";
                        target.style.color = "#ffffff";
                      }}
                      onMouseLeave={(e) => {
                        const target = e.target as HTMLButtonElement;
                        target.style.background = "#ffffff";
                        target.style.color = "#ef4444";
                      }}
                      title="パラメータを削除"
                    >
                      ×
                    </button>
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  padding: "16px",
                  textAlign: "center",
                  color: "#9ca3af",
                  fontStyle: "italic",
                  fontSize: "0.75rem",
                  background: "#f8fafc",
                  borderRadius: "4px",
                  border: "1px dashed #d1d5db",
                }}
              >
                パラメータが登録されていません
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};